/**
 * RichTextEditorEditor
 * Configuration editor for rich-text-editor control
 * Handles toolbar configuration and plugin settings, plus Plan/05 §3C's opt-in Expression Builder
 * toggle (the shared `ExpressionBuilderSection`, the same component `TextEditor`/`TextareaEditor`
 * already use — and, since `RICH_TEXT_EXPRESSION_DEFAULT_FORMAT` became `'text'`, with the same
 * effective defaults as well; only the help text differs, because this control's *name* invites an
 * assumption about markup that its live implementation does not support).
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';
import { ExpressionBuilderSection } from '../ExpressionBuilderSection';
import { RICH_TEXT_EXPRESSION_DEFAULT_FORMAT } from '@atlas-forms/player-components-react';

interface ToolbarButton {
  id: string;
  label: string;
  icon?: string;
}

interface RichTextEditorConfig {
  toolbar?: ToolbarButton[];
  plugins?: string[];
  defaultContent?: string;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: number;
}

function renderRichTextEditorEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as RichTextEditorConfig;

  const defaultToolbar: ToolbarButton[] = [
    { id: 'bold', label: 'Bold' },
    { id: 'italic', label: 'Italic' },
    { id: 'underline', label: 'Underline' },
    { id: 'strikethrough', label: 'Strikethrough' },
    { id: 'heading', label: 'Heading' },
    { id: 'list', label: 'List' },
    { id: 'link', label: 'Link' },
    { id: 'image', label: 'Image' },
  ];

  const toolbar = config.toolbar || defaultToolbar;
  const plugins = config.plugins || ['image', 'link', 'table'];

  const handleAddToolbarButton = useCallback(() => {
    const newToolbar = [...toolbar, { id: `button-${toolbar.length}`, label: 'Button' }];
    onConfigChange('toolbar', newToolbar);
  }, [toolbar, onConfigChange]);

  const handleRemoveToolbarButton = useCallback(
    (index: number) => {
      onConfigChange('toolbar', toolbar.filter((_, i) => i !== index));
    },
    [toolbar, onConfigChange],
  );

  const handleUpdateToolbarButton = useCallback(
    (index: number, field: 'id' | 'label', value: string) => {
      const updated = toolbar.map((b, i) =>
        i === index ? { ...b, [field]: value } : b,
      );
      onConfigChange('toolbar', updated);
    },
    [toolbar, onConfigChange],
  );

  const handlePluginsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const pluginList = e.currentTarget.value
        .split('\n')
        .map((p) => p.trim())
        .filter((p) => p);
      onConfigChange('plugins', pluginList);
    },
    [onConfigChange],
  );

  return (
    <>
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Rich Text Editor Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="placeholder-input" className="af-properties-tab__label">
          Placeholder Text
        </label>
        <input
          id="placeholder-input"
          type="text"
          value={config.placeholder || ''}
          onChange={(e) => onConfigChange('placeholder', e.target.value)}
          placeholder="Enter placeholder..."
          className="af-properties-tab__input"
          aria-label="Placeholder text"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-height-input" className="af-properties-tab__label">
          Minimum Height (pixels)
        </label>
        <input
          id="min-height-input"
          type="number"
          value={config.minHeight || 200}
          onChange={(e) => onConfigChange('minHeight', Number(e.target.value))}
          min="50"
          className="af-properties-tab__input"
          aria-label="Minimum height"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={config.readOnly ?? false}
            onChange={(e) => onConfigChange('readOnly', e.target.checked)}
          />
          Read-Only Mode
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Toolbar Buttons
        </label>
        {toolbar.map((button, i) => (
          <div key={i} className="af-properties-tab__toolbar-button-row">
            <input
              type="text"
              value={button.id}
              onChange={(e) => handleUpdateToolbarButton(i, 'id', e.target.value)}
              className="af-properties-tab__input af-properties-tab__toolbar-id"
              placeholder="button-id"
              aria-label={`Button ${i + 1} ID`}
            />
            <input
              type="text"
              value={button.label}
              onChange={(e) => handleUpdateToolbarButton(i, 'label', e.target.value)}
              className="af-properties-tab__input af-properties-tab__toolbar-label"
              placeholder="Button Label"
              aria-label={`Button ${i + 1} label`}
            />
            <button
              onClick={() => handleRemoveToolbarButton(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddToolbarButton}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Button
        </button>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="plugins-textarea" className="af-properties-tab__label">
          Plugins (one per line)
        </label>
        <textarea
          id="plugins-textarea"
          value={plugins.join('\n')}
          onChange={handlePluginsChange}
          placeholder="image&#10;link&#10;table"
          rows={4}
          className="af-properties-tab__textarea"
          aria-label="Plugins"
        />
      </div>
    </div>

    {/*
      Plan/05 §3C. Same shared component `TextEditor`/`TextareaEditor` use — the config key, the
      gate (`config.expressionBuilder.enabled === true`), the runtime trigger and, as of the
      output-format fix below, the default format too.

      `RICH_TEXT_EXPRESSION_DEFAULT_FORMAT` is the *same constant* `FormField.tsx`'s
      `case 'rich-text-editor':` passes `ExpressionBuilderTrigger`, imported rather than restated.
      These two used to be independent literals kept in step by a comment saying they must be
      changed together; now they cannot disagree — which is why changing that one constant from
      `'html'` to `'text'` moved this panel's "(default)" marker with it, automatically.
    */}
    <ExpressionBuilderSection
      control={control}
      onConfigChange={onConfigChange}
      defaultOutputFormat={RICH_TEXT_EXPRESSION_DEFAULT_FORMAT}
      outputFormatHelp={
        'Text by default. Despite this control’s name, the field it renders is a plain text box, ' +
        'and the same XSS filter is applied to builder output as to typed input — so markup is ' +
        'stored HTML-encoded and shown as literal tags either way. Choosing HTML is still ' +
        'supported, but it makes the builder re-wrap the stored value in a paragraph every time ' +
        'the field is edited, so it is only worth setting if something downstream decodes and ' +
        'renders the stored markup itself.'
      }
    />
    </>
  );
}

export const RichTextEditorEditor: PropertyEditor = {
  type: 'rich-text-editor',
  label: 'Rich Text Editor',
  description: 'Configure rich text editor toolbar, plugins and Expression Builder support',
  render: renderRichTextEditorEditor,
};
